import * as React from "react";
import { ResponsiveToggleContainerModel, ResponsiveToggleContainerProps } from "./typings";
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "bernie-view";
import {
  BlossomComponentFactory,
  BlossomComponentFactoryProps,
} from "components/flexFramework/domain/BlossomComponentFactory";
import { BlossomComponents } from "components/flexFramework/BlossomComponents/BlossomComponents";
import { UitkExpandoCard } from "uitk-react-expando";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkHeading } from "uitk-react-text";
import { getExtraClassNameBasedOnParameters } from "../SimpleContainer/utilityStyles";

export interface ResponsiveToggleContainerContext {
  modulesHaveBorder?: boolean;
}

const defaultContext = {
  modulesHaveBorder: false,
};

export const ResponsiveToggleContainerContext = React.createContext<ResponsiveToggleContainerContext>(defaultContext);
const DEFAULT_WIDTH = "100";

export const ResponsiveToggleContainer = (props: ResponsiveToggleContainerProps) => {
  const blossomComponent = BlossomComponentFactory.get(props as BlossomComponentFactoryProps);
  const context = props.context;

  if (!blossomComponent) {
    return null;
  }

  const model = blossomComponent.model as ResponsiveToggleContainerModel;
  const { name, mobileWidth, title, width, innerTitle, tabletWidth, desktopWidth, collapsed } = model;
  const modules = blossomComponent.getBlossomComponents();
  const modulesHaveBorder = model.hasBorder;

  // Return null for regions with no title, inner title and all "empty" modules
  if (
    (title &&
      innerTitle &&
      modules &&
      modules.length > 0 &&
      modules.filter((module) => module.shouldRender()).length === 0) ||
    !modules.length
  ) {
    return null;
  }

  const gridWidth = regionWidthToGridMap[width || DEFAULT_WIDTH];
  const gridTabletWidth = regionWidthToGridMap[tabletWidth || DEFAULT_WIDTH];
  const gridMobileWidth = regionWidthToGridMap[mobileWidth || DEFAULT_WIDTH];

  const commonClassNames = ["uitk-grid uitk-cell", getExtraClassNameBasedOnParameters(model)]
    .filter((classes) => classes !== "")
    .join(" ");

  const ResponsiveContainerContent = (containerProps: { includeTitle: boolean; classNames: string }) => (
    <div className={` ${commonClassNames} ${containerProps.classNames} `} role="region">
      {title && (
        <UitkSpacing>
          <UitkHeading
            level={2}
            size={700}
            className={` ${!containerProps.includeTitle && "is-visually-hidden"} uitk-cell all-cell-1-1`}
          >
            {title}
          </UitkHeading>
        </UitkSpacing>
      )}
      {innerTitle && (
        <UitkSpacing padding={{ inlinestart: "three" }}>
          <UitkHeading level={3} size={700} className="uitk-cell all-cell-1-1">
            {innerTitle}
          </UitkHeading>
        </UitkSpacing>
      )}
      <BlossomComponents blossomComponent={blossomComponent} context={context} />
    </div>
  );
  const [isVisible, setIsVisible] = React.useState(collapsed ? false : true);
  const toggle = React.useCallback(() => setIsVisible(!isVisible), [isVisible]);

  const flexContent = (
    <>
      {title && (
        <UitkSpacing>
          <UitkHeading level={2} size={700} className="uitk-cell all-cell-1-1">
            {title}
          </UitkHeading>
        </UitkSpacing>
      )}
      {innerTitle && (
        <UitkSpacing padding={{ inlinestart: "three" }}>
          <UitkHeading level={3} size={700} className="uitk-cell all-cell-1-1">
            {innerTitle}
          </UitkHeading>
        </UitkSpacing>
      )}
      <BlossomComponents blossomComponent={blossomComponent} context={context} />
    </>
  );

  return (
    <ResponsiveToggleContainerContext.Provider value={{ modulesHaveBorder }}>
      <Viewport>
        {mobileWidth === "hidden" ? (
          <ViewSmall />
        ) : (
          <ViewSmall>
            <UitkSpacing padding={{ block: "three", inline: "three" }}>
              <div className="ResponsiveToggleContainer SimpleContainer">
                <UitkExpandoCard
                  isVisible={isVisible}
                  handleClick={toggle}
                  expandoTitle={title}
                  expandoDescription={
                    <ResponsiveContainerContent
                      classNames={`SimpleContainer ${
                        name === "dx-page-header" ? " elevation-4" : ""
                      } ${gridMobileWidth}`}
                      includeTitle={false}
                    />
                  }
                />
              </div>
            </UitkSpacing>
          </ViewSmall>
        )}

        {tabletWidth === "hidden" ? (
          <ViewMedium />
        ) : (
          <ViewMedium>
            <UitkSpacing padding={{ block: "three", inline: "three" }}>
              <div
                className={`ResponsiveToggleContainer SimpleContainer ${
                  name === "dx-page-header" ? " elevation-4" : ""
                } ${commonClassNames} ${gridTabletWidth} `}
                role="region"
              >
                {flexContent}
              </div>
            </UitkSpacing>
          </ViewMedium>
        )}

        {desktopWidth === "hidden" ? (
          <ViewLarge />
        ) : (
          <ViewLarge>
            <UitkSpacing padding={{ block: "three", inline: "three" }}>
              <div
                className={`${commonClassNames} ${gridWidth} ${
                  name === "dx-page-header" ? "elevation-4" : " SimpleContainer ResponsiveToggleContainer"
                }`}
                role="region"
              >
                {flexContent}
              </div>
            </UitkSpacing>
          </ViewLarge>
        )}
      </Viewport>
    </ResponsiveToggleContainerContext.Provider>
  );
};

/**
 * Return the classes corresponding to a specific region. This is
 * a temporary solution until Module groups are implemented
 * @param {string} regionName - Name of region
 * @return {string} classes to decorate region
 */
/* istanbul ignore next */

const regionWidthToGridMap = {
  "25": "all-cell-1-4",
  "33": "all-cell-1-3",
  "50": "all-cell-1-2",
  "66": "all-cell-2-3",
  "75": "all-cell-3-4",
  "100": "all-cell-1-1",
};

export default ResponsiveToggleContainer;
